import axios, { AxiosError } from 'axios';
import { getCookies, removeCookies, setCookies } from '../utility/cookies';
import { toast } from 'react-toastify';

const apiInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const NetworkErrors = [
  {
    pathName: '/auth/admin/verify/otp',
    errors: 'Network issue. Please try again later.',
  },
  {
    pathName: '/auth/user/create',
    errors: 'Unable to add user. Network error encountered. Please try again.',
  },
  {
    pathName: '/auth/user/update/',
    errors:
      'Unable to update user. Network error encountered. Please try again.',
  },
  {
    pathName: '/auth/users/list',
    errors: 'Sorting failed due to network issues. Please refresh.',
  },
  {
    pathName: '/auth/user/delete/',
    errors: 'Deletion failed. Please try again later.',
  },
];

const processQueue = (error: AxiosError | null) => {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });
  failedQueue = [];
};

apiInstance.interceptors.request.use(
  config => {
    if (isRefreshing) {
      return Promise.reject(new Error('Token refresh in progress'));
    }

    const token = getCookies('accessToken');
    const userShard = getCookies('user-shard');
    if (userShard) {
      config.headers['user-shard'] = userShard;
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error),
);

apiInstance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // Handle offline state
    if (!window.navigator.onLine) {
      const errorData = NetworkErrors.find(error =>
        originalRequest.url?.includes(error.pathName),
      );
      if (errorData) {
        return Promise.reject({
          response: {
            data: {
              message: errorData.errors,
            },
          },
        });
      } else {
        return Promise.reject({
          response: {
            data: {
              message: 'Network error. Please check your connection and retry.',
            },
          },
        });
      }
    }

    if (
      error?.response?.data?.customErrorNumber === 1009 &&
      window.location.pathname !== '/login'
    ) {
      localStorage.clear();
      toast.error('Session expired. Please log in again to continue.');
      const role = getCookies('role');
      window.location.href = role === 'ADMIN' ? '/admin-login' : '/login';
      removeCookies('user-shard');
      removeCookies('accessToken');
      removeCookies('refreshToken');

      return Promise.reject(error);
    }

    if (
      error.response?.status === 401 ||
      (error?.response?.data?.status === 401 && !originalRequest._retry)
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => apiInstance(originalRequest))
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = getCookies('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/auth/refresh/token`,
          {},
          {
            headers: {
              Authorization: `Bearer ${refreshToken}`,
            },
          },
        );

        const { accessToken, refreshToken: newRefreshToken } =
          response.data.result;

        setCookies('accessToken', accessToken);

        apiInstance.defaults.headers.common['Authorization'] =
          `Bearer ${accessToken}`;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        processQueue(null);
        return apiInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as AxiosError);
        const role = getCookies('role');

        window.location.href = role === 'ADMIN' ? '/admin-login' : '/login';
        removeCookies('accessToken');
        removeCookies('refreshToken');
        removeCookies('user-shard');
        localStorage.clear();

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default apiInstance;
